import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import GoogleSignInButton from "../components/GoogleSignInButton";
import SEO from "../components/SEO";

/**
 * Sign-up page — Google primary, email as fallback (collapsed by default).
 * Cuts the form down to one click for 95% of new users.
 */
export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/templates";
  const [showEmail, setShowEmail] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) return toast.error("Password should be at least 6 characters");
    setBusy(true);
    try {
      await signup(form.name, form.email, form.password);
      toast.success("Account created");
      nav(next);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="signup-page">
      <Navbar />
      <div className="px-6 py-12 md:py-16 max-w-md mx-auto">
        <div className="text-[10px] uppercase tracking-[0.4em] text-stone-500 mb-3">Begin</div>
        <h1 className="font-serif-display text-4xl md:text-5xl font-light mb-3">Create your studio.</h1>
        <p className="text-stone-600 text-sm mb-8">Build your wedding website free. Pay ₹999 only when you&apos;re ready to publish.</p>

        {/* Primary CTA — one-click Google */}
        <GoogleSignInButton next={next} label="Continue with Google" testId="signup-google-btn" />

        <div className="my-5 flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-stone-400">
          <div className="flex-1 h-px bg-stone-200" />
          <span>or</span>
          <div className="flex-1 h-px bg-stone-200" />
        </div>

        {!showEmail ? (
          <button
            type="button"
            onClick={() => setShowEmail(true)}
            data-testid="signup-show-email"
            className="w-full py-3 text-xs uppercase tracking-[0.3em] border border-stone-300 text-stone-700 hover:bg-stone-100"
          >
            Sign up with email
          </button>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="signup-email-form">
            <input data-testid="signup-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="w-full px-4 py-3 bg-white border border-stone-300 outline-none focus:border-stone-900 text-sm" />
            <input data-testid="signup-email" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="w-full px-4 py-3 bg-white border border-stone-300 outline-none focus:border-stone-900 text-sm" />
            <input data-testid="signup-password" type="password" placeholder="Password (min 6)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="w-full px-4 py-3 bg-white border border-stone-300 outline-none focus:border-stone-900 text-sm" />
            <button data-testid="signup-submit" disabled={busy} className="w-full py-3 bg-stone-900 text-white uppercase tracking-[0.3em] text-xs hover:bg-stone-800 disabled:opacity-60">{busy ? "Creating..." : "Create account"}</button>
          </form>
        )}

        <div className="mt-8 text-sm text-stone-600">
          Already have an account? <Link to="/login" className="underline underline-offset-4 font-semibold" data-testid="goto-login">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
